/* eslint-disable no-console */
import { LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { create } from 'zustand';

import { Configuration } from '../constants';

export const ENTRA_REDIRECT_PATH = '/auth/redirect';
export const ENTRA_POPUP_RELAY_PATH = '/auth/popup-relay';

const EMBEDDED_IFRAME_BRIDGE_TIMEOUT_MS = 4000;

const isEmbeddedInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

let msalInstance: PublicClientApplication;

export const getEntraAuthInstance = async () => {
  if (msalInstance) {
    return msalInstance;
  }

  const embedded = isEmbeddedInIframe();

  msalInstance = new PublicClientApplication({
    auth: {
      clientId: Configuration.entraClientId,
      authority: Configuration.entraAuthority,
      redirectUri: `${window.location.origin}${ENTRA_REDIRECT_PATH}`,
      postLogoutRedirectUri: `${window.location.origin}${ENTRA_REDIRECT_PATH}`,
      popupRelayUri: embedded ? `${window.location.origin}${ENTRA_POPUP_RELAY_PATH}` : undefined
    },
    cache: {
      cacheLocation: 'localStorage' // This configures where your cache will be stored
    },
    system: {
      iframeBridgeTimeout: embedded ? EMBEDDED_IFRAME_BRIDGE_TIMEOUT_MS : undefined,
      loggerOptions: {
        logLevel: LogLevel.Error,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              console.log(message);
          }
        }
      }
    }
  });

  await msalInstance.initialize();

  return msalInstance;
};

const INTERACTIVE_LOGIN_COOLDOWN_MS = 1000 * 60 * 5;

let lastInteractiveLoginStartedAt = 0;

export const isInteractiveLoginOnCooldown = () =>
  Date.now() - lastInteractiveLoginStartedAt < INTERACTIVE_LOGIN_COOLDOWN_MS;

export const markInteractiveLoginStarted = () => {
  lastInteractiveLoginStartedAt = Date.now();
};

export const clearInteractiveLoginCooldown = () => {
  lastInteractiveLoginStartedAt = 0;
};

export const useAuthToken = create<{
  idToken: string;
  provider: 'entra' | 'amp' | '';
}>(() => ({ idToken: '', provider: '' }));
export const getIdToken = () => useAuthToken.getState().idToken;
